import { partition } from 'rxjs';
import { filter } from 'rxjs/operators';
import Modal from '@roo/roo/Modal';

import {
    orderMasterMessageHub as messageHub,
    PushCode,
    NewOrderPushData,
    getWmPoiId,
    canBeHandledByCurrentPoi,
    canBeHandledByCurrentSinglePoi,
    currentLoginWmPoiId,
} from '@services/messageHub/instance';

import {
    UnprocessedOrderType,
    DispatchExceptionCode,
} from '@business/pages/OrderManage/shared/types';
import { getPrintTimingSetting } from '@projects/Business/pages/OrderManage/shared/helper';

import * as LX from '@utils/lxWrapper';
import { ifPoiEnableAutoAcceptLocally } from '@utils/poiHelper';
import {
    reportOrderFingerprint,
    PC_ORDER_FINGERPRINT_ID,
} from '@utils/report/orderFingerprintReport';
import * as orderReport from '@utils/report/orderArrivalRateReport';
import { recordReceiptInfo } from '@utils/report/orderReceiptReport';
import * as orderPrintService from '@utils/print/orderPrintService';
import { isInApp } from '@utils/getEnv';
import { getGrayStateByToken, GRAY_AB_TOKEN } from '@utils/grayAB';
import request from '@utils/axiosWrapper';
import * as Ids from '@utils/ids';

import * as API from '../../connection/eventHandler/order/api';
import {
    confirmOrderAndPrint,
    flashWindow,
    updateOrderCount,
} from '../../connection/eventHandler/order/order';
import {
    addOrderNotification,
    addWeakNotification,
    addStrongNotification,
} from '../../connection/eventHandler/pushNotification';
import {
    ApplyRefundPushData,
    PushOrderUserRole,
    PushRefundType,
} from '../../connection/push/pushDataTypes';
import {
    orderPrepCountUpdateSubject,
    orderDeliveryCountUpdateSubject,
    orderProcessCountUpdateSubject,
    orderRefundCountUpdateSubject,
    orderFoodSafeProcess4PoiCountUpdateSubject,
    orderNearDeliveryTimeRemindSubject,
    orderRemindCountUpdateSubject,
    mealSlowOrderCountUpdateSubject,
    orderNewOrderCountSubject,
    otherExceptionOrderCountUpdateSubject,
    preOrderPrintOnRemindSubject,
    orderAllCountUpdateSubject,
    orderAllCountUpdateDelaySubject,
    orderAllCountUpdateDelaySubject2,
} from '../subjects';
import bellService from '../../service/bell';
import openRefundUrgeModal from './components/RefundUrgeModal';
import { FRAME_NAME, sendMessageToSubFrame } from '../../utils/subFrames';

/** 是否单门店 */
const isSinglePoi = !Ids.isMultiPoi();
/** 300 账号门店增加待出餐 tab灰度 */
const isGray300AB = getGrayStateByToken(GRAY_AB_TOKEN.MEAL_TAB_GRAW);
/** 单店或者命中300账号门店增加待出餐tab灰度 */
const limitPreMealCount = isSinglePoi || isGray300AB;

export default () => {
    // Code:1 新订单
    const [autoAcceptPoiOrder$, noneAutoAcceptPoiOrder$] = partition(
        messageHub.getPushSource<NewOrderPushData>(PushCode.NEW_ORDER_1),
        ({ origin: message }) => ifPoiEnableAutoAcceptLocally(getWmPoiId(message)),
    );

    /**
     * 上报新订单详情指纹
     */
    const reportNewOrderDetailFingerprint = {
        success: (orderViewId: string, orderPoiId: string) => {
            reportOrderFingerprint(
                orderViewId,
                orderPoiId,
                PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                0,
            );
        },
        fail: (orderViewId: string, orderPoiId: string) => {
            reportOrderFingerprint(
                orderViewId,
                orderPoiId,
                PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                1,
            );
        },
    };

    // 自动接单门店订单处理流程
    autoAcceptPoiOrder$.subscribe(({ origin: message, detail }) => {
        // 订单指纹上报
        try {
            reportOrderFingerprint(
                message.wmOrderViewId,
                getWmPoiId(message),
                PC_ORDER_FINGERPRINT_ID.NEW_ORDER_PUSH_ARRIVE,
                0,
            );
        } catch (e) {
            // let it go
        }

        // 上报新订单详情指纹
        if (!detail) {
            reportNewOrderDetailFingerprint.fail(message.wmOrderViewId, getWmPoiId(message));
            return;
        }
        reportNewOrderDetailFingerprint.success(message.wmOrderViewId, getWmPoiId(message));

        const {
            businessType,
            orderInfo: {
                wmOrderViewId,
                basicVo: { wmPoiId },
                orderTag,
            },
        } = detail;

        orderReport.reportEvent(orderReport.ORDER_ARRIVE_EVENT.PUSH_ARRIVED, [wmOrderViewId]);

        // 接单并打印
        confirmOrderAndPrint(
            {
                wmPoiId,
                wmOrderViewId,
                businessType,
            },
            'auto_push',
        );

        // 响铃
        if (orderTag === 8) {
            // 优享送铃声
            bellService.playBell('yxsNewOrder');
        } else {
            // 普通铃声
            if (message.isPreOrder) {
                bellService.playBell('autonewpreorder');
            } else {
                bellService.playBell('autoneworder');
            }
        }

        // 订单消息
        addOrderNotification({
            content: '美团外卖自动接单，刚刚为您接到新订单了',
            href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                ? '/new_fe/orderbusiness#/order/unprocessed'
                : '/new_fe/order#/order/unprocessed',
            wmPoiId: `${wmPoiId}`,
        });

        // 订单回执
        recordReceiptInfo(
            [
                {
                    wmPoiId: `${wmPoiId}`,
                    wmOrderViewId,
                },
            ],
            true,
        );

        // 订单到达率上报
        orderReport.reportEvent(orderReport.ORDER_ARRIVE_EVENT.PUSH_DETAIL_SUCCESS, [
            wmOrderViewId,
        ]);

        // 唤起客户端或闪烁图标
        flashWindow();
    });

    // 非自动接单门店订单处理流程
    noneAutoAcceptPoiOrder$.subscribe(({ origin: message }) => {
        orderReport.reportEvent(orderReport.ORDER_ARRIVE_EVENT.PUSH_ARRIVED, [
            message.wmOrderViewId,
        ]);
        updateOrderCount({
            [UnprocessedOrderType.NewOrder]: '+1',
            noticeInner: true,
        });

        // 履约新形态
        // 待接单
        updateOrderCount({
            [UnprocessedOrderType.New]: '+1',
            noticeInner: true,
        });
        // 全部订单没有计数，只用于列表刷新
        updateOrderCount({
            [UnprocessedOrderType.All]: '1',
            noticeInner: true,
        });
        if (message.isPreOrder) {
            bellService.playBell('newpreorder');
        } else {
            bellService.playBell('neworder');
        }
        addOrderNotification({
            content: '您有新订单了',
            href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                ? '/new_fe/orderbusiness#/order/unprocessed?tab=new'
                : '/new_fe/order#/order/unprocessed?tab=newOrder',
            wmPoiId: getWmPoiId(message),
        });
        flashWindow();
    });

    // Code:3 确认订单push
    messageHub
        .getPushSource(PushCode.ORDER_CONFIRMED_3)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            /** 餐饮新形态灰度 */
            const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
            if (UNIFIED_ORDER_GRAY) {
                const messageInfoDelay = message
                    ? Object.assign(message, {
                          UpdateType: [
                              ...(limitPreMealCount ? [UnprocessedOrderType.PrepMeal] : []),
                              UnprocessedOrderType.PrepLogistics,
                          ],
                      })
                    : message;
                orderAllCountUpdateDelaySubject.next(messageInfoDelay);

                if (Ids.isMultiPoi()) {
                    const messageInfo = message
                        ? Object.assign(message, {
                              UpdateType: [UnprocessedOrderType.New],
                          })
                        : message;
                    orderAllCountUpdateDelaySubject2.next(messageInfo);
                } else {
                    const messageInfo = message
                        ? Object.assign(message, {
                              UpdateType: [UnprocessedOrderType.New],
                          })
                        : message;
                    orderAllCountUpdateSubject.next(messageInfo);
                }
            } else {
                orderPrepCountUpdateSubject.next(message);
                orderDeliveryCountUpdateSubject.next(message);
                orderProcessCountUpdateSubject.next(message);
                orderNewOrderCountSubject.next(message);
            }

            // 优享送订单被其他设备接单后播放特定铃声，此场景下后端会屏蔽 code=999,showType=40 的消息发送逻辑
            if (message.orderTag === 8) {
                bellService.playBell('yxsNewOrder');
            }
        });
    // Code:4 顾客退款申请、申诉
    messageHub.getPushSource<ApplyRefundPushData>(PushCode.APPLY_REFUND_4).subscribe(message => {
        if (canBeHandledByCurrentPoi(message)) {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
        }
        const refundType = parseInt(message.refund_type, 10);
        if (refundType === PushRefundType.NORMAL) {
            if (canBeHandledByCurrentPoi(message)) {
                /** 餐饮新形态灰度 */
                const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
                if (UNIFIED_ORDER_GRAY) {
                    const messageInfo = message
                        ? Object.assign(message, {
                              UpdateType: [
                                  UnprocessedOrderType.AfterSaleCanAppeal,
                                  UnprocessedOrderType.Refund,
                              ],
                          })
                        : message;
                    orderAllCountUpdateSubject.next(messageInfo);
                } else {
                    orderRefundCountUpdateSubject.next(message);
                }
            }
            const wmPoiId = getWmPoiId(message);
            // 代购商家自动退款
            if (wmPoiId && wmPoiId !== 'unknown' && message.wmOrderViewId) {
                API.autoAgreeRefund(wmPoiId, message.wmOrderViewId).catch(() => null);
            }
        } else if (refundType === PushRefundType.REMINE_20_MINUTES) {
            orderPrintService.printRefundTips(
                message.order_push_seq,
                message.end_time,
                message.wmOrderViewId,
            );
        } else if (refundType === PushRefundType.EXCEED_DELIVERY_TIME_30_MINUTES) {
            bellService.playBell('cancel');
            orderPrintService.printOrderCancelTips(
                message.order_push_seq,
                message.wmOrderViewId,
                '订单超建议送达时间30分钟以外',
            );
        } else if (refundType === PushRefundType.CANCEL_ORDER_IN_1_MINUTE) {
            bellService.playBell('cancel');
            orderPrintService.printOrderCancelTips(
                message.order_push_seq,
                message.wmOrderViewId,
                '接单1分钟内顾客取消',
            );
        } else if (refundType === PushRefundType.TIMEOUT_AUTO_REFUND) {
            bellService.playBell('cancel');
            orderPrintService.printOrderCancelTips(
                message.order_push_seq,
                message.wmOrderViewId,
                '顾客退款请求超时未处理',
            );
        } else if (refundType === PushRefundType.SPEED_REFUND_RISK_NOTICE) {
            Modal.confirm({
                title: '极速退款提醒',
                children: message.message,
                cancelText: '去关闭',
                confirmText: '继续开通',
                onCancel: () => {
                    // eslint-disable-next-line waimaie/no-direct-access-localstorage
                    localStorage.setItem('speedRefundFromPush', 'true');
                    window.location.hash = '/v2/shop/manage/speedRefund?fromPush=true';
                },
            });
        } else if (refundType === PushRefundType.OTHER) {
            bellService.playBell('cancel');
            orderPrintService.printOrderCancelTips(
                message.order_push_seq,
                message.wmOrderViewId,
                '商家开通极速退款服务，顾客退款系统自动通过',
            );
        } else if (refundType === PushRefundType.PRE_ORDER_AUTO_CANCEL) {
            bellService.playBell('cancel');
            orderPrintService.printOrderCancelTips(
                message.order_push_seq,
                message.wmOrderViewId,
                '用户在商家备餐前申请预订单退款，系统自动通过',
            );
        } else if (refundType === PushRefundType.PRE_ORDER_AUTO_CANCEL_AT_REST) {
            bellService.playBell('cancel');
            orderPrintService.printOrderCancelTips(
                message.order_push_seq,
                message.wmOrderViewId,
                '用户在商家备餐前申请预订单退款，系统自动通过',
            );
        } else if (refundType === PushRefundType.PRE_ORDER_AUTO_PARTIAL_REFUND) {
            bellService.playBell('preorder-partial-refund/3');
            orderPrintService.printOrderPartialRefundTips(
                message.order_push_seq,
                message.wmOrderViewId,
                '用户在商家备餐前申请预订单部分退款，系统自动通过',
            );
        } else if (refundType === PushRefundType.PRE_ORDER_AUTO_PARTIAL_REFUND_AT_REST) {
            bellService.playBell('preorder-partial-refund/1');
            orderPrintService.printOrderPartialRefundTips(
                message.order_push_seq,
                message.wmOrderViewId,
                '用户在商家备餐前申请预订单部分退款，系统自动通过',
            );
        } else if (refundType === PushRefundType.AMOUNT_REFUND) {
            /** 金额退退款打印 */
            orderPrintService.printAmountRefundTips(message.order_push_seq, message.wmOrderViewId);
        }
    });

    // Code:6 预订单通知
    messageHub
        .getPushSource<{ user_role: PushOrderUserRole; wmOrderViewId: string }>(
            PushCode.PRE_ORDER_NOTICE_6,
        )
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
            /** 餐饮新形态灰度 */
            const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
            if (UNIFIED_ORDER_GRAY) {
                if (limitPreMealCount) {
                    const messageInfo = Object.assign(message || {}, {
                        UpdateType: [
                            UnprocessedOrderType.PrepMeal,
                            UnprocessedOrderType.PreOrderToMeal,
                        ],
                    });
                    orderAllCountUpdateDelaySubject.next(messageInfo);
                }
            } else {
                orderProcessCountUpdateSubject.next(message);
                orderPrepCountUpdateSubject.next(message);
            }
            orderNearDeliveryTimeRemindSubject.next(message);

            if (
                isInApp &&
                getPrintTimingSetting(getWmPoiId(message)) === 'all' &&
                message.wmOrderViewId &&
                message.user_role
            ) {
                preOrderPrintOnRemindSubject.next({
                    user_role: message.user_role, // eslint-disable-line @typescript-eslint/camelcase
                    wmPoiId: getWmPoiId(message),
                    wmOrderViewId: message.wmOrderViewId,
                });
            }
        });

    // Code: 9 收到新的顾客催单
    messageHub
        .getPushSource(PushCode.REMIND_ORDER_9)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            /** 餐饮新形态灰度 */
            const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
            if (UNIFIED_ORDER_GRAY) {
                const messageInfo = message
                    ? Object.assign(message, {
                          UpdateType: [UnprocessedOrderType.Remind],
                      })
                    : message;
                orderAllCountUpdateSubject.next(messageInfo);
            } else {
                orderRemindCountUpdateSubject.next(message);
            }
        });

    // Code:12 订单配送状态更新提示
    messageHub.getPushSource(PushCode.LOGISTICS_STATUS_CHANGE_12).subscribe(message => {
        if (canBeHandledByCurrentPoi(message)) {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
            /** 餐饮新形态灰度 */
            const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);

            if (UNIFIED_ORDER_GRAY) {
                const messageInfo = message
                    ? Object.assign(message, {
                          UpdateType: [
                              ...(limitPreMealCount ? [UnprocessedOrderType.PrepMeal] : []),
                              UnprocessedOrderType.PrepLogistics,
                          ],
                      })
                    : message;
                orderAllCountUpdateDelaySubject.next(messageInfo);
            } else {
                orderPrepCountUpdateSubject.next(message);
                orderDeliveryCountUpdateSubject.next(message);
            }
        }

        if (message.dispatcherType) {
            const dispatcherType = parseInt(message.dispatcherType, 10);
            // dispatcherType 是否属于跑腿配送业务线（0无，1跑腿，2其他）
            if (dispatcherType === 1) {
                /** 跑腿取消、改派提醒 */
                const logisticsStatus = parseInt(message.logisticsStatus, 10);
                let eventModule = '';
                if (logisticsStatus === 100) {
                    eventModule = 'b_waimai_e_4ndd1naq';
                } else if (logisticsStatus === 200) {
                    eventModule = 'b_waimai_e_dqpav46c';
                }
                if (eventModule) {
                    LX.reportMV(`${eventModule}_mv`, 'c_waimai_e_hwefx10v', {
                        poi_id: getWmPoiId(message),
                    });
                    addOrderNotification({
                        content: message.message,
                        wmPoiId: getWmPoiId(message),
                        readReceiptConfig: {
                            mc: `${eventModule}_mc`,
                            cid: 'c_waimai_e_hwefx10v',
                            custom: { poi_id: getWmPoiId(message) },
                        },
                    });
                }
            }
        }
    });

    // Code:16 订单状态为8(顾客确认收货)
    messageHub
        .getPushSource(PushCode.ORDER_COMPLETED_16)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            /** 餐饮新形态灰度 */
            const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
            if (UNIFIED_ORDER_GRAY) {
                if (limitPreMealCount) {
                    const messageInfo = Object.assign(message || {}, {
                        UpdateType: [UnprocessedOrderType.PrepMeal],
                    });
                    orderAllCountUpdateDelaySubject.next(messageInfo);
                }
            } else {
                orderPrepCountUpdateSubject.next(message);
                orderProcessCountUpdateSubject.next(message);
            }
        });

    // Code:17 订单状态为9(订单取消)
    messageHub
        .getPushSource(PushCode.ORDER_CANCELLED_17)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
            if (message.cancelOrderSound === 'true') {
                bellService.playBell('cancel');
            }
            /** 餐饮新形态灰度 */
            const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
            if (UNIFIED_ORDER_GRAY) {
                orderAllCountUpdateDelaySubject.next(message);
            } else {
                orderPrepCountUpdateSubject.next(message);
                orderDeliveryCountUpdateSubject.next(message);
                orderProcessCountUpdateSubject.next(message);
                orderRemindCountUpdateSubject.next(message);
            }

            addOrderNotification({
                content: `${message.wmOrderViewId}号订单已被取消，请勿配送`,
                href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                    ? '/new_fe/orderbusiness#/order/history'
                    : '/new_fe/business#/order/history',
                wmPoiId: getWmPoiId(message),
            });
        });
    //Code:24 结算信息变更
    messageHub
        .getPushSource(PushCode.CHARGE_DETAIL_CHANGE_24)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
        });
    // Code:26 配送异常通知（异常产生和异常恢复都会有push）
    messageHub
        .getPushSource(PushCode.LOGISTICS_EXCEPTION_26)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
            if (message.recovery === '0') {
                // 配送异常新增
                updateOrderCount({
                    [UnprocessedOrderType.DeliveryException]: '+1',
                    noticeInner: true,
                });

                // 履约新形态配送异常key
                updateOrderCount({
                    [UnprocessedOrderType.LogisticsAbnormal]: '+1',
                    noticeInner: true,
                });

                /** 是否众包催取餐push */
                const isZbUrgeTakeMeal =
                    message.zb_special_message &&
                    getGrayStateByToken(GRAY_AB_TOKEN.URGE_TAKE_MEAL, message.wmPoiId);

                let bellType = 'exception';
                let tipsText = '有订单配送异常！请尽快处理';

                if (isZbUrgeTakeMeal) {
                    bellType = 'paotui-exception';
                    tipsText = '您有订单配送异常，请及时联系骑手或点击催取餐';
                } else {
                    const exceptionType = window.parseInt(message.exception_type, 10);
                    // eslint-disable-next-line default-case
                    switch (exceptionType) {
                        case DispatchExceptionCode.DISPATCH_OUT_TIME15:
                        case DispatchExceptionCode.DISPATCH_OUT_TIME30:
                        case DispatchExceptionCode.DISPATCH_OUT_FALLBACK:
                            bellType = 'noDispatcherAcceptOrder';
                            break;
                        case DispatchExceptionCode.GET_MEAL_OUT_TIME:
                        case DispatchExceptionCode.PRE_ORDER_COMBINE:
                            bellType = 'dispatcherNotTakeMeal';
                            break;
                        case DispatchExceptionCode.TO_STORE_OUT_TIME:
                            bellType = 'dispatcherNotArrival';
                            break;
                        case DispatchExceptionCode.DISPATCHER_CANCEL:
                            bellType = 'dispatcherCancel';
                            break;
                        case DispatchExceptionCode.DISPATCHER_TRANSFER:
                            bellType = 'dispatcherTransfer';
                            break;
                        case DispatchExceptionCode.SUPER_BIG_ORDER_DELIVERY_CANCEL:
                            bellType = 'superBigOrderDeliveryCancel';
                            break;
                        case DispatchExceptionCode.DISPATCHER_CANCEL_BEFORE_MEAL_TAKEN_REPEATEDLY:
                            bellType = 'dispatchPaused';
                            break;
                        case DispatchExceptionCode.DISPATCHER_CANCEL_AFTER_MEAL_TAKEN_REPEATEDLY:
                            bellType =
                                Number(message.new) === 1
                                    ? 'dispatchStopped'
                                    : 'superBigOrderDeliveryCancel';
                            break;
                        case DispatchExceptionCode.DISPATCHER_CANCEL_BEFORE_MEAL_TAKEN:
                            bellType = 'dispatcherCancelAndRedispatch';
                            break;
                        case DispatchExceptionCode.DISPATCHER_CANCEL_AFTER_MEAL_TAKEN:
                            bellType = 'dispatcherCancel';
                            break;
                        case DispatchExceptionCode.DISPATCH_SELL_OUT:
                            bellType = 'dispatcherSellOut';
                            break;
                        case DispatchExceptionCode.DISPATCH_SELF_PICK:
                            bellType = 'dispatchSelfPick';
                            break;
                        case DispatchExceptionCode.DISPATCH_ZI_PEI:
                            bellType = 'dispatcherZiPei';
                            break;
                    }
                }

                bellService.playBell(bellType);
                addOrderNotification({
                    content: tipsText,
                    href: getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                        ? '/new_fe/orderbusiness#/order/unprocessed?tab=logisticsAbnormal'
                        : '/new_fe/order#/order/unprocessed?tab=deliveryException',
                    wmPoiId: message.showPoiName ? getWmPoiId(message) : undefined,
                });
            } else {
                // 配送异常消除
                updateOrderCount({
                    [UnprocessedOrderType.DeliveryException]: '-1',
                    noticeInner: true,
                });
                // 履约新形态配送异常key
                updateOrderCount({
                    [UnprocessedOrderType.LogisticsAbnormal]: '-1',
                    noticeInner: true,
                });

                const exceptionType = Number(message.exception_type);
                switch (exceptionType) {
                    case DispatchExceptionCode.DISPATCHER_CANCEL_BEFORE_MEAL_TAKEN:
                        bellService.playBell('newDispatcher');
                        break;
                    default:
                        break;
                }
            }
        });

    // Code:28 确认出餐提醒
    messageHub
        .getPushSource(PushCode.MAKE_MEAL_DONE_28)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
            /** 餐饮新形态灰度 */
            const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
            if (UNIFIED_ORDER_GRAY) {
                if (limitPreMealCount) {
                    const messageInfo = Object.assign(message || {}, {
                        UpdateType: [UnprocessedOrderType.PrepMeal],
                    });
                    orderAllCountUpdateDelaySubject.next(messageInfo);
                }
            } else {
                orderPrepCountUpdateSubject.next(message);
            }
        });

    // Code:29 出餐异常提醒（骑手上报商家出餐慢）
    messageHub
        .getPushSource(PushCode.MAKE_MEAL_SLOW_REPORTED_29)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            /** 餐饮新形态灰度 */
            const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
            if (UNIFIED_ORDER_GRAY) {
                const messageInfo = message
                    ? Object.assign(message, {
                          UpdateType: [UnprocessedOrderType.MealAbnormal],
                      })
                    : message;
                orderAllCountUpdateSubject.next(messageInfo);
            } else {
                mealSlowOrderCountUpdateSubject.next(message);
            }
        });

    // Code:30 强制结束卡餐生效状态push通知
    messageHub.getPushSource(PushCode.FORCE_QUIT_MAKE_MEAL_SLOW_STATE_30).subscribe(message => {
        addStrongNotification({
            title: message.title,
            content: message.message,
            wmPoiId: getWmPoiId(message),
            wmEMsgTraceId: message.wmEMsgTraceId,
            wmEPoiMsgId: message.wmEPoiMsgId,
        });
    });

    // Code:31 防漏单提醒（订单即将超时取消）
    messageHub
        .getPushSource(PushCode.ORDER_WILL_TIMEOUT_31)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(() => {
            bellService.playBell('orderWillTimeout');
        });

    //Code:32 企客配送类型变更
    messageHub
        .getPushSource(PushCode.QIKE_DELIVERY_CAPACITY_CHANGE_32)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
        });

    // Code:51 配送爆单提醒
    messageHub.getPushSource(PushCode.DELIVERY_CAPACITY_INSUFFICIENT_51).subscribe(message => {
        addWeakNotification({
            title: '运力不足',
            content: message.message,
            wmPoiId: getWmPoiId(message),
            wmEMsgTraceId: message.wmEMsgTraceId,
            wmEPoiMsgId: message.wmEPoiMsgId,
        });
    });

    // Code:52 配送爆单恢复
    messageHub.getPushSource(PushCode.DELIVERY_CAPACITY_BACK_TO_NORMAL_52).subscribe(message => {
        addWeakNotification({
            title: '运力恢复',
            content: message.message,
            wmPoiId: getWmPoiId(message),
            wmEMsgTraceId: message.wmEMsgTraceId,
            wmEPoiMsgId: message.wmEPoiMsgId,
        });
    });

    // Code:53 卡餐提醒
    messageHub
        .getPushSource(PushCode.MAKE_MEAL_SLOW_RISK_REMIND_53)
        .pipe(filter(message => canBeHandledByCurrentSinglePoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
            const sceneType = message.sceneType;
            if (sceneType === '1' || sceneType === '10') {
                bellService.playBell('mealSlow_bigOrder');
            } else if (sceneType === '2' || sceneType === '20') {
                bellService.playBell('mealSlow_multiSku');
            } else if (sceneType === '3') {
                bellService.playBell('mealSlow_sharkOrder');
            } else if (sceneType === '4') {
                bellService.playBell('mealSlow_riderWait');
            } else if (sceneType === '30') {
                bellService.playBell('mealSlow_other');
            }
        });

    // Code:55 打印小票重新设置成功
    messageHub.getPushSource(PushCode.RECEIPT_PRINT_CONFIG_CHANGED_55).subscribe(() => {
        // eslint-disable-next-line waimaie/no-direct-access-localstorage
        localStorage.removeItem('waimai_e_print_config'); // 清空打印配置信息（打印的时候重拉接口获取即可）
    });

    //Code:56 跑腿权益生效状态变更
    messageHub
        .getPushSource(PushCode.PAOTUI_RIGHTS_TAKE_EFFECT_56)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
        });

    //Code:61 配送压单状态变更
    messageHub
        .getPushSource(PushCode.DELIVERY_SUSPEND_STATUS_CHANGE_61)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
        });

    // Code:62 新订单预计收入为负提醒
    messageHub
        .getPushSource(PushCode.NEGATIVE_INCOME_REMIND_62)
        .pipe(filter(message => canBeHandledByCurrentSinglePoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
            bellService.playBell('negativeIncome');
        });

    // Code:63 定责状态变更
    messageHub
        .getPushSource(PushCode.ORDER_DUTY_STATUS_63)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
        });

    // Code:64 预订单被骑手接单提醒
    messageHub
        .getPushSource(PushCode.PRE_ORDER_ACCEPTED_BY_RIDER_64)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
            bellService.playBell('preorderAcceptedByRider');
            /** 餐饮新形态灰度 */
            const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
            if (UNIFIED_ORDER_GRAY) {
                if (limitPreMealCount) {
                    const messageInfo = Object.assign(message || {}, {
                        UpdateType: [
                            UnprocessedOrderType.PrepMeal,
                            UnprocessedOrderType.PreOrderToMeal,
                        ],
                    });
                    orderAllCountUpdateDelaySubject.next(messageInfo);
                }
            } else {
                orderPrepCountUpdateSubject.next(message);
            }

            if (
                isInApp &&
                getPrintTimingSetting(message.wmPoiId) === 'all' &&
                message.wmOrderViewId &&
                message.user_role
            ) {
                preOrderPrintOnRemindSubject.next({
                    user_role: message.user_role, // eslint-disable-line @typescript-eslint/camelcase
                    wmPoiId: getWmPoiId(message),
                    wmOrderViewId: message.wmOrderViewId,
                });
            }
        });

    // Code:66 解除拉黑骑手提醒
    messageHub
        .getPushSource(PushCode.UNBLOCK_RIDER_NOTICE_66)
        .pipe(filter(message => canBeHandledByCurrentSinglePoi(message)))
        .subscribe(({ orderViewId, extraTitle, extraMessage, blacklistLinkUrl }) => {
            request.get('/gw/api/logistics/common/rider/save/pageView', {
                params: { orderViewId },
            });
            Modal.confirm({
                title: extraTitle,
                children: extraMessage,
                confirmText: '前往限制接单骑手名单',
                onConfirm: () => {
                    window.location.hash = blacklistLinkUrl;
                },
                cancelText: '不用了',
            });
        });

    //Code:99 有「待退款申诉」订单通知时，重新拉取订单数据
    messageHub
        .getPushSource(PushCode.REFUND_CAN_APPEAL_99)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            /** 餐饮新形态灰度 */
            const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
            if (UNIFIED_ORDER_GRAY) {
                const messageInfo = Object.assign(message || {}, {
                    UpdateType: [UnprocessedOrderType.AfterSaleCanAppeal],
                });
                orderAllCountUpdateDelaySubject.next(messageInfo);
            }
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
        });

    // Code:103 自动发配送失败
    messageHub
        .getPushSource(PushCode.AUTO_SEND_DELIVERY_FAIL_103)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            bellService.playBell('autoSendDeliveryFail');
            addOrderNotification({
                content: '您有订单自动发配送失败，请及时处理',
                wmPoiId: getWmPoiId(message),
            });
        });

    // Code:124 用户发起 ETA 变更申请
    messageHub
        .getPushSource(PushCode.ETA_CHANGE_REQUEST_124)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            /** 餐饮新形态灰度 */
            const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
            if (UNIFIED_ORDER_GRAY) {
                const messageInfo = message
                    ? Object.assign(message, {
                          UpdateType: [UnprocessedOrderType.Other],
                      })
                    : message;
                orderAllCountUpdateSubject.next(messageInfo);
            } else {
                /** 更新计数 */
                otherExceptionOrderCountUpdateSubject.next(message);
            }

            if (currentLoginWmPoiId !== '-1') {
                if (message.ueta_status === '1') {
                    /** 播放铃声（用户发起申请+单店模式） */
                    bellService.playBell('etaChangeRequest');
                }
                if (message.ueta_status === '15') {
                    /** 播放铃声（用户发起申请+单店模式） */
                    if (`${message?.soundType}` === '1') {
                        bellService.playBell('preOrderETAChangeSound');
                    }
                    if (isInApp && message?.ueta_print === '1') {
                        //自动打印小票
                        preOrderPrintOnRemindSubject.next({
                            user_role: message.user_role || -1, // eslint-disable-line @typescript-eslint/camelcase
                            wmPoiId: getWmPoiId(message),
                            wmOrderViewId: message.wmOrderViewId,
                        });
                    }
                }
            }
        });

    //Code:3005 转自配按钮支持实时刷新
    messageHub
        .getPushSource(PushCode.SELF_DELIVERY_BUTTON_AUTO_REFRESH_3005)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
        });

    // Code:3006 食安相关状态变更（赔付状态、放心吃状态）
    messageHub
        .getPushSource(PushCode.FOOD_SAFETY_STATUS_CHANGE_3006)
        .pipe(filter(message => canBeHandledByCurrentSinglePoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
            /** 餐饮新形态灰度 */
            const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
            if (UNIFIED_ORDER_GRAY) {
                const messageInfo = message
                    ? Object.assign(message, {
                          UpdateType: [
                              UnprocessedOrderType.FoodSafeAppealForPoi,
                              UnprocessedOrderType.FoodSafeProcess4Poi,
                          ],
                      })
                    : message;
                orderAllCountUpdateSubject.next(messageInfo);
            } else {
                orderFoodSafeProcess4PoiCountUpdateSubject.next({ shouldUpdateCount: true });
            }
        });

    // Code:3007 更换骑手
    messageHub
        .getPushSource(PushCode.DISPATCHER_CHANGE_3007)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
            const extraParams = JSON.parse(message.extraParams || '{}');
            const result = extraParams.result;
            if (result === '1') {
                bellService.playBell('dispatcherChangeSuccess');
            } else if (result === '2') {
                bellService.playBell('dispatcherChangeFail');
            }
        });

    // Code:3008 预订单备餐兜底提醒
    messageHub
        .getPushSource(PushCode.PRE_ORDER_PREP_MEAL_FALLBACK_REMIND_3008)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            /** 餐饮新形态灰度 */
            const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
            if (UNIFIED_ORDER_GRAY) {
                if (limitPreMealCount) {
                    const messageInfo = Object.assign(message || {}, {
                        UpdateType: [
                            UnprocessedOrderType.PrepMeal,
                            UnprocessedOrderType.PreOrderToMeal,
                        ],
                    });
                    orderAllCountUpdateDelaySubject.next(messageInfo);
                }
            } else {
                orderPrepCountUpdateSubject.next(message);
            }
        });

    // Code:3011 转自配可用次数变化，刷新订单列表（刷新当前列表中所有订单的配送信息）
    messageHub
        .getPushSource(PushCode.SELF_DELIVERY_AVAILABLE_LIMIT_CHANGED_3011)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
        });

    // Code:3012 退款催审核
    messageHub
        .getPushSource(PushCode.REFUND_URGE_3012)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
            bellService.playBell('refundUrge');
            try {
                const {
                    refundUrgeTitle,
                    refundUrgeTips,
                    refundUrgeTemplate,
                    orderDateAndSeq,
                    agreeRefundDeadlineTime,
                    wmOrderViewId,
                } = JSON.parse(message.extraParams || '{}');
                openRefundUrgeModal({
                    title: refundUrgeTitle,
                    tips: refundUrgeTips,
                    template: refundUrgeTemplate,
                    orderDateAndSeq,
                    agreeRefundDeadlineTime: Number(agreeRefundDeadlineTime),
                    wmOrderViewId,
                });
            } catch {
                // No ops.
            }
        });

    // Code:3013 （通用的）刷新订单信息
    messageHub
        .getPushSource(PushCode.REFRESH_ORDER_INFO_3013)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            sendMessageToSubFrame(FRAME_NAME.HASH, message);
        });
    // Code:3020 负订单消息通知
    messageHub
        .getPushSource(PushCode.NEGATIVE_ORDER_NOTICE_3020)
        .pipe(filter(message => canBeHandledByCurrentPoi(message)))
        .subscribe(message => {
            // 订单消息
            addOrderNotification({
                content: message.alarmContent,
                wmPoiId: getWmPoiId(message),
            });
            // 负订单铃声
            bellService.playBell('negativeOrder');
            orderPrintService.printOrderNegativeTips(
                message.printTitle,
                message.wmOrderViewId,
                message.printContent,
            );
        });

    // Code:3018 动态tab设置需要透传刷新页面(账号维度)
    messageHub.getPushSource(PushCode.TAB_SETTING_REFRESH_3018).subscribe(() => {
        setTimeout(() => {
            window.location.reload();
        }, 100);
    });
};
